import { Input } from '@mui/material';
import Box from '@mui/material/Box';
import MuiSlider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import { ChangeEvent, FC, ReactNode, useId } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    decimalEntryRefusalMessage,
    useDecimalEntry,
} from './Inputs/useDecimalEntry';

interface SliderProps {
    label: string | ReactNode;
    min: number;
    max: number;
    value: number;
    step?: number;
    onChange: (value: number) => void;
    disabled?: boolean;
    showDecimals?: boolean;
    showValueInput?: boolean;
    testId?: string;
}

/**
 * A slider with a value field. A number typed into the field is applied while
 * it is typed when it is in [min, max], unless it may also be a whole number
 * in range written with a thousands separator (`1,500`), which is refused
 * with a hint ({@link useDecimalEntry}). While the field shows an entry it
 * does not accept, the value in effect when the entry began is applied
 * again, so no part of a refused entry stays applied, and leaving the field
 * shows the applied value. Why an entry is refused is shown under the field
 * and is the field's accessible description.
 */
export const Slider: FC<SliderProps> = ({
    label,
    min,
    max,
    value,
    step = 1,
    onChange,
    disabled = false,
    showDecimals = true,
    showValueInput = true,
    testId = 'custom-slider-component',
}) => {
    const messageId = useId();
    const entry = useDecimalEntry({
        value,
        // The slider's value is never empty: the entry applies only numbers,
        // and the value from before an edit is one.
        onChange: (next) => onChange(next as number),
        min,
        max,
        format: (shown) =>
            shown === null
                ? ''
                : showDecimals
                  ? shown.toFixed(2)
                  : shown.toString(),
        applyWhileTyping: true,
        refuseNonEntryCharacters: false,
    });

    const handleSliderChange = (_: Event, newValue: number | number[]) => {
        if (disabled) {
            return;
        }
        entry.apply(Math.min(Math.max(newValue as number, min), max));
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (disabled) {
            return;
        }
        entry.onTextChange(event.target.value);
    };

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                opacity: disabled ? 0.5 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
            }}
        >
            <Box display="flex" justifyContent="space-between" mb={-1}>
                <Box component="span" fontSize={13}>
                    {label}
                </Box>
                {showValueInput && (
                    <Input
                        name="slider-value"
                        sx={{
                            width: 50,
                            textAlign: 'right',
                            fontSize: 12,
                            p: 0,
                            border: 'none',
                            '&.MuiInput-root::before': {
                                borderBottom: 'none',
                            },
                            'input::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                            },
                        }}
                        inputProps={{
                            step: step,
                            min: min,
                            max: max,
                            type: 'text',
                            'aria-label': 'slider value input',
                            'aria-describedby': entry.isInvalid
                                ? messageId
                                : undefined,
                            style: { border: 'none', textAlign: 'right' },
                        }}
                        value={entry.text}
                        onFocus={entry.onFocus}
                        onChange={handleInputChange}
                        onBlur={entry.onBlur}
                        error={entry.isInvalid}
                        disabled={disabled}
                    />
                )}
            </Box>
            {entry.isInvalid && entry.refusal !== null && (
                <Typography id={messageId} color="error" fontSize={12}>
                    <FormattedMessage
                        {...decimalEntryRefusalMessage(entry.refusal, {
                            min,
                            max,
                            maxFractionDigits: Number.POSITIVE_INFINITY,
                        })}
                    />
                </Typography>
            )}
            <MuiSlider
                value={entry.accepted ?? entry.inEffect ?? value}
                onChange={handleSliderChange}
                aria-labelledby={testId}
                data-testid={testId}
                step={step}
                min={min}
                max={max}
                disabled={disabled}
                sx={(theme) => ({
                    '& .MuiSlider-thumb': {
                        border: disabled
                            ? `2px solid ${theme.palette.text.secondary}`
                            : '2px solid primary.main',
                    },
                })}
            />
        </Box>
    );
};
